import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";

const AUTOPLAY_MS = 5500;

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const goStep = (step: number) => {
    setIndex((prev) => (prev + step + total) % total);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 45) {
      goStep(delta < 0 ? 1 : -1);
    }
    touchStartX.current = null;
  };

  const active = heroSlides[index];

  return (
    <section
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[420px] md:h-[600px] w-full">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 精选推荐背景图`}
              title={`${slide.title} - 适合夫妻二人看的影片`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/40 to-foreground-950/15"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full items-center">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-line text-xs"></i>
                {active.badge}
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-5xl font-semibold leading-tight text-background-50">
                {active.title}
              </h1>
              <p className="mt-4 max-w-lg text-sm md:text-lg leading-relaxed text-background-100">
                {active.subtitle}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.movieId}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-play-circle-line text-base"></i>
                  查看影片详情
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/60 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20 cursor-pointer"
                >
                  <i className="ri-compass-3-line text-base"></i>
                  浏览全部影片
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goStep(-1)}
          className="absolute left-3 md:left-6 top-1/2 z-20 flex h-8 w-8 md:h-10 md:w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/25 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goStep(1)}
          className="absolute right-3 md:right-6 top-1/2 z-20 flex h-8 w-8 md:h-10 md:w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/25 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? "w-7 bg-primary-500" : "w-2 bg-background-50/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}